import { Typography } from '@mui/material';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import * as React from 'react';
import NumberFormat, { InputAttributes } from 'react-number-format';

import { COLORS } from '@/styles/tokens/colors';

import { FormControl } from '../FormControl';
import {
  BaseCurrencyInputProps,
  FormControlTextInputProps,
  HelpTextVariant,
} from '../inputTypes';

interface CurrencyInputProps extends Omit<FormControlTextInputProps, 'type'> {
  label: string;
  id?: string;
  // the presence of contextualHelp indicates that there's help text related to this input, which
  // will cause the "info" icon to show up next to the input label
  contextualHelp?: JSX.Element;
  decimalScale?: number;
  errorMessage?: string;
  helpText?: string;
  helpTextVariant?: HelpTextVariant;
  allowNegative?: boolean;
  hideLabel?: boolean;
}

interface NumberFormatProps {
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  label: string;
  id?: string;
  contextualHelp?: JSX.Element;
  decimalScale?: number;
  allowNegative?: boolean;
}

const NumberFormatInput = React.forwardRef<
  NumberFormat<InputAttributes>,
  NumberFormatProps
>(function NumberFormatCustom(props, ref) {
  const { onChange, decimalScale = 2, allowNegative = false, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
      decimalScale={decimalScale}
      type="tel"
      allowNegative={allowNegative}
    />
  );
});

function BaseCurrencyInput({
  // see here: https://github.com/mui/material-ui/issues/12946
  // stripping off onKeyUp and onKeyDown is required here because TextField doesn't actually implement those props.
  onKeyUp: _onKeyUp,
  onKeyDown: _onKeyDown,
  decimalScale,
  allowNegative,
  endAdornment,
  ...inputProps
}: BaseCurrencyInputProps) {
  const { disabled } = inputProps;
  const disabledStyle = disabled ? { background: COLORS.GRAY[50] } : {};
  const mergedSx = Object.assign({ background: 'white' }, disabledStyle);
  return (
    <TextField
      InputProps={{
        inputComponent: NumberFormatInput as unknown as NonNullable<
          NonNullable<TextFieldProps['InputProps']>['inputComponent']
        >,
        startAdornment: (
          <Typography variant="body-xs" component="span">
            $
          </Typography>
        ),
        endAdornment,
        sx: mergedSx,

        inputProps: {
          'data-testid': `currency-input-${inputProps.name ?? ''}`,
          allowNegative,
        },
      }}
      variant="outlined"
      inputProps={{
        decimalScale,
      }}
      {...inputProps}
    />
  );
}

export function CurrencyInput({
  label,
  contextualHelp,
  errorMessage,
  helpText,
  helpTextVariant,
  hideLabel,
  ...inputProps
}: CurrencyInputProps) {
  return (
    <FormControl<BaseCurrencyInputProps>
      component={BaseCurrencyInput}
      inputProps={inputProps}
      errorMessage={errorMessage}
      contextualHelp={contextualHelp}
      required={inputProps.required}
      helpText={helpText}
      helpTextVariant={helpTextVariant}
      id={inputProps.id}
      label={label}
      hideLabel={hideLabel}
    />
  );
}
